import React from "react";
import {
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  PaperClipOutlined,
  UploadOutlined,
  UserAddOutlined,
} from "@ant-design/icons";
import {
  Button,
  Col,
  Select,
  Divider,
  Drawer,
  Form,
  Input,
  List,
  Row,
  Space,
  Tag,
  Timeline,
  Typography,
  Upload,
  message,
} from "antd";

import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  createContact,
  updateContact,
  listTags,
} from "../../redux/actions/contactsAction";
import { listContactEnrollments } from "../../redux/actions/sequencesAction";
import { ENROLLMENT_STATUS_COLORS } from "../../pages/App/Sequences/sequenceUtils";
import {
  fetchConsentEvents,
  openConsentProofPdf,
} from "../../redux/actions/consentAction";
import { privatePostMultipart } from "../../utils/apiHelper";

const { Text } = Typography;

const CONSENT_EVENT_LABELS = {
  granted: "Consent granted",
  withdrawn: "Consent withdrawn",
  double_optin_sent: "Double opt-in email sent",
  double_optin_confirmed: "Double opt-in confirmed",
  legacy_notice_sent: "DPDP legacy notice sent",
  imported_declared: "Declared at import",
};

const CONSENT_EVENT_COLORS = {
  granted: "green",
  withdrawn: "red",
  double_optin_sent: "blue",
  double_optin_confirmed: "green",
  legacy_notice_sent: "blue",
  imported_declared: "gray",
};

function ConsentTimeline({ contactId }) {
  const dispatch = useDispatch();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (!contactId) return;
    setLoading(true);
    dispatch(fetchConsentEvents(contactId))
      .then(setEvents)
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, [contactId, dispatch]);

  const downloadProof = async () => {
    setExporting(true);
    try {
      await dispatch(openConsentProofPdf(contactId));
    } catch {
      message.error("Failed to open consent proof.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="small">
      <Space style={{ width: "100%", justifyContent: "space-between" }}>
        <Typography.Text strong>Consent timeline</Typography.Text>
        <Button
          size="small"
          icon={<DownloadOutlined />}
          loading={exporting}
          onClick={downloadProof}
        >
          Consent proof
        </Button>
      </Space>
      {loading ? (
        <Typography.Text type="secondary">Loading…</Typography.Text>
      ) : events.length === 0 ? (
        <Typography.Text type="secondary">
          No consent events recorded yet.
        </Typography.Text>
      ) : (
        <Timeline
          style={{ marginTop: 8 }}
          items={events.map((event) => ({
            color: CONSENT_EVENT_COLORS[event.event] || "gray",
            children: (
              <Space direction="vertical" size={0}>
                <Space>
                  <Tag color={CONSENT_EVENT_COLORS[event.event] || "default"}>
                    {CONSENT_EVENT_LABELS[event.event] || event.event}
                  </Tag>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {new Date(event.occurredAt).toLocaleString()}
                  </Typography.Text>
                </Space>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {event.purpose} · {event.source?.type || "unknown source"}
                </Typography.Text>
              </Space>
            ),
          }))}
        />
      )}
    </Space>
  );
}

export default function FormContact({ type, formData, fetchRecords }) {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();
  const availableTags = useSelector((state) => state.contacts.tags);
  const [pendingFile, setPendingFile] = useState(null);
  const [existingAttachment, setExistingAttachment] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [enrollmentsList, setEnrollmentsList] = useState([]);
  const [enrollmentsLoading, setEnrollmentsLoading] = useState(false);

  const showDrawer = () => {
    setOpen(true);
  };
  const onClose = () => {
    setOpen(false);
    form.resetFields();
  };
  const onSubmit = async (values) => {
    setSubmitting(true);
    try {
      let contactId = type === "edit" ? formData._id : null;
      if (type === "create") {
        const created = await dispatch(createContact(values));
        contactId = created._id;
      } else {
        await dispatch(updateContact(values, formData._id));
      }

      if (pendingFile && contactId) {
        const fd = new FormData();
        fd.append("file", pendingFile, pendingFile.name);
        await privatePostMultipart(`/contacts/${contactId}/attachment`, fd);
      }

      fetchRecords();
      setOpen(false);
      form.resetFields();
      setPendingFile(null);
      setExistingAttachment(null);
    } catch (e) {
      console.log(e);
      message.error("Failed to save contact.");
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (open) {
      dispatch(listTags({}))
        .then(() => {})
        .catch((e) => console.log(e));
      setPendingFile(null);
      setExistingAttachment(type === "edit" ? formData?.attachment || null : null);
      if (type === "edit" && formData?._id) {
        setEnrollmentsLoading(true);
        dispatch(listContactEnrollments(formData._id))
          .then((result) => {
            setEnrollmentsList(result.records || []);
            setEnrollmentsLoading(false);
          })
          .catch(() => setEnrollmentsLoading(false));
      } else {
        setEnrollmentsList([]);
      }
    }
  }, [open, dispatch, type, formData]);

  return (
    <>
      {type === "create" ? (
        <Button
          icon={<UserAddOutlined />}
          title="Add Contact"
          onClick={showDrawer}
        >
          Add
        </Button>
      ) : (
        <Button type="link" onClick={showDrawer} title="Edit Contact">
          <EditOutlined />
        </Button>
      )}
      <Drawer
        title={type === "create" ? "New Contact" : "Edit Contact"}
        width="min(720px, 90vw)"
        placement="right"
        maskClosable={false}
        destroyOnClose={true}
        onClose={onClose}
        open={open}
        styles={{
          body: {
            paddingBottom: 80,
          },
        }}
        extra={
          <Space>
            <Button onClick={onClose}>Cancel</Button>
            <Button type="primary" loading={submitting} onClick={form.submit}>
              Save
            </Button>
          </Space>
        }
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ ...formData }}
          onFinish={onSubmit}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="firstName"
                label="First Name"
                rules={[
                  {
                    required: true,
                    whitespace: true,
                    message: "Please enter first name",
                  },
                ]}
              >
                <Input placeholder="Please enter first name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="lastName"
                label="Last Name"
                rules={[
                  {
                    required: false,
                    whitespace: true,
                    message: "Please enter last name",
                  },
                ]}
              >
                <Input placeholder="Please enter last name" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="emailAddress"
                label="Email Address"
                rules={[
                  {
                    required: true,
                    whitespace: true,
                    message: "Please enter email address",
                  },
                  {
                    type: "email",
                    message: "Please enter a valid email address",
                  },
                ]}
              >
                <Input placeholder="Please enter email address" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="phoneNumber"
                label="Phone Number"
                rules={[
                  {
                    required: false,
                    message: "Please enter phone number",
                  },
                  {
                    pattern: new RegExp("^[0-9]{10,11}$", "g"),
                    message: "Please enter a valid phone number",
                  },
                ]}
              >
                <Input placeholder="Please enter phone number" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                name="tags"
                label="Tags"
                rules={[
                  {
                    required: true,
                    message: "Please enter contact tags",
                  },
                ]}
              >
                <Select
                  mode="tags"
                  style={{
                    width: "100%",
                  }}
                  placeholder="Please enter contact tags"
                  options={availableTags.map((tag) => ({
                    value: tag,
                    label: tag,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="remarks" label="Your Remarks">
                <Input.TextArea
                  rows={4}
                  placeholder="Please enter your remarks, notes or comments"
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item label="Attachment" help="Sent as a personal attachment alongside any campaign this contact receives.">
                <Upload
                  beforeUpload={(file) => {
                    setPendingFile(file);
                    return false;
                  }}
                  showUploadList={false}
                >
                  <Button icon={<UploadOutlined />}>
                    {existingAttachment || pendingFile ? "Replace file" : "Add file"}
                  </Button>
                </Upload>
                {(existingAttachment || pendingFile) && (
                  <List
                    size="small"
                    style={{ marginTop: 8 }}
                    dataSource={[
                      pendingFile
                        ? { label: pendingFile.name, pending: true }
                        : { label: existingAttachment.filename, pending: false },
                    ]}
                    renderItem={(item) => (
                      <List.Item
                        actions={
                          pendingFile
                            ? [
                                <Button
                                  key="remove"
                                  type="text"
                                  danger
                                  size="small"
                                  icon={<DeleteOutlined />}
                                  onClick={() => setPendingFile(null)}
                                />,
                              ]
                            : []
                        }
                      >
                        <PaperClipOutlined style={{ marginRight: 8 }} />
                        {item.label}
                        {item.pending ? " (will upload on save)" : ""}
                      </List.Item>
                    )}
                  />
                )}
              </Form.Item>
            </Col>
            {type === "edit" && formData?._id && (
              <Col span={24}>
                <ConsentTimeline contactId={formData._id} />
              </Col>
            )}
          </Row>
        </Form>

        {type === "edit" && (
          <>
            <Divider />
            <Text strong>Sequence enrolments</Text>
            {enrollmentsLoading ? (
              <div style={{ marginTop: 8 }}>
                <Text type="secondary">Loading...</Text>
              </div>
            ) : enrollmentsList.length === 0 ? (
              <div style={{ marginTop: 8 }}>
                <Text type="secondary">Not enrolled in any sequence.</Text>
              </div>
            ) : (
              <List
                size="small"
                style={{ marginTop: 8 }}
                dataSource={enrollmentsList}
                renderItem={(item) => (
                  <List.Item>
                    <Space direction="vertical" size={0}>
                      <Text>{item.sequenceName}</Text>
                      <Space size="small">
                        <Tag color={ENROLLMENT_STATUS_COLORS[item.status] || "default"}>
                          {item.status}
                        </Tag>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          Step {item.currentStep}
                          {item.exitReason ? ` · ${item.exitReason}` : ""}
                        </Text>
                      </Space>
                    </Space>
                  </List.Item>
                )}
              />
            )}
          </>
        )}
      </Drawer>
    </>
  );
}
